import React, { useEffect, useRef, useState, useMemo } from 'react';
import { X, Plus } from 'lucide-react';
import { assetsService, locationsService } from '../../services';
import { Asset, QuickAddAssetItem, Location } from '../../types';
import './QuickAddSpotlight.css';

interface QuickAddSpotlightProps {
  mode: 'location' | 'asset';
  facilityId: string;
  parentAsset?: Asset | null;
  onSuccess: (asset: Asset) => void;
  onLocationCreated?: (location: Location) => void;
  onClose: () => void;
}

const LOCATION_ICONS: Record<string, string> = {
  building: '🏢',
  floor: '🧱',
  room: '🚪',
};

const ASSET_ICONS: Record<string, string> = {
  equipment: '⚙️',
  hvac: '🌡️',
  electrical: '⚡',
  plumbing: '🔧',
  fire_safety: '🔥',
  pool: '🏊',
  sauna: '🧖',
};

const chipIcon = (item: QuickAddAssetItem) => {
  if (item.is_location_asset) return LOCATION_ICONS[item.type] ?? '📍';
  return ASSET_ICONS[item.type] ?? '📦';
};

const QuickAddSpotlight: React.FC<QuickAddSpotlightProps> = ({
  mode,
  facilityId,
  parentAsset,
  onSuccess,
  onLocationCreated,
  onClose,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [selectedType, setSelectedType] = useState<QuickAddAssetItem | null>(null);
  const [pickingType, setPickingType] = useState(false);
  const [locationType, setLocationType] = useState<Location['type']>('room');
  const [catalog, setCatalog] = useState<QuickAddAssetItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // All presets for this mode
  const allPresets = useMemo(
    () => catalog.filter((item) => (mode === 'location' ? item.is_location_asset : !item.is_location_asset)),
    [catalog, mode],
  );

  // Filter presets by name as user types
  const filteredPresets = useMemo(() => {
    const q = name.trim().toLowerCase();
    if (!q) return allPresets;
    const words = q.split(/\s+/).filter(word => !/^\d+$/.test(word));
    return allPresets.filter(item => words.some(word => item.name.toLowerCase().includes(word)));
  }, [allPresets, name]);

  const showCreateOption = name.trim().length > 0;

  useEffect(() => {
    inputRef.current?.focus();
    if (mode === 'location') { setCatalogLoading(false); return; }
    assetsService
      .getQuickAddCatalog()
      .then(setCatalog)
      .catch(() => setCatalog([]))
      .finally(() => setCatalogLoading(false));
  }, [mode]);

  const handlePresetClick = (item: QuickAddAssetItem) => {
    if (pickingType) { void submit(item, name); return; }
    setSelectedType(item);
    inputRef.current?.focus();
  };

  const submit = async (preset: QuickAddAssetItem | null, customName: string) => {
    const trimmed = customName.trim();
    if (!trimmed) { setError('Please enter a name.'); return; }

    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'location') {
        const created = await locationsService.createLocation({ facility_id: facilityId, name: trimmed, type: locationType });
        onLocationCreated?.(created);
        onClose();
        return;
      }
      const newAsset = await assetsService.createAsset({
        facility_id: facilityId,
        parent_asset_id: parentAsset?.id ?? undefined,
        asset_type_id: preset?.asset_type_id ?? undefined,
        name: trimmed,
        type: preset?.type ?? 'equipment',
        ...(preset ? {} : { asset_type_id: null }),
        description: preset?.description ?? undefined,
        location: preset?.location_hint ?? undefined,
      });
      onSuccess(newAsset);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create. Please try again.');
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !submitting) {
      if (mode === 'location' || selectedType) void submit(selectedType, name);
      else setPickingType(true);
    }
    if (e.key === 'Escape') onClose();
  };

  return (
    <div className="spotlight-overlay" onClick={onClose}>
      <div
        className="spotlight-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'location' ? 'Add location' : 'Add asset'}
      >
        {/* Input row */}
        <div className="spotlight-input-row">
          <span className="spotlight-mode-icon">{mode === 'location' ? '📍' : '📦'}</span>
          <input
            ref={inputRef}
            className="spotlight-input"
            placeholder={
              mode === 'location'
                ? 'e.g. "Ground Floor", "Plant Room"…'
                : 'e.g. "Boiler 1", "Pool Pump A"…'
            }
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            aria-label={mode === 'location' ? 'Location name' : 'Asset name'}
            autoComplete="off"
          />
          <button className="spotlight-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {error && <p className="spotlight-error">{error}</p>}

        {mode === 'location' && <label>Location type<select className="form-input" value={locationType} onChange={event => setLocationType(event.target.value as Location['type'])}>
          {['building', 'wing', 'floor', 'zone', 'room'].map(type => <option key={type} value={type}>{type.replace(/^./, c => c.toUpperCase())}</option>)}
        </select></label>}
        {selectedType && <p>Type: {selectedType.name} <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedType(null); setPickingType(true); }}>Change type</button></p>}
        {pickingType && <p>Add '{name}' as…</p>}
        {/* Preset chips */}
        <div className="spotlight-presets">
          {catalogLoading ? (
            <p className="spotlight-loading">Loading presets…</p>
          ) : (
            <>
              {mode === 'asset' && !selectedType && (filteredPresets.length > 0 || pickingType) && (
                <div className="spotlight-chips">
                  {(pickingType && filteredPresets.length === 0 ? allPresets : filteredPresets).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="spotlight-chip"
                      disabled={submitting}
                      onClick={() => handlePresetClick(item)}
                    >
                      <span aria-hidden="true" className="spotlight-chip__icon">{chipIcon(item)}</span>
                      <span className="spotlight-chip__name">{item.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {pickingType && <button className="btn btn-secondary btn-sm" disabled={submitting} onClick={() => submit(null, name)}>Other/untyped</button>}
              {/* "Create new" option always visible when something is typed */}
              {showCreateOption && (
                <button
                  type="button"
                  className="spotlight-create-row"
                  onClick={() => { if (mode === 'location' || selectedType) void submit(selectedType, name); else setPickingType(true); }}
                  disabled={submitting}
                >
                  <span className="spotlight-create-icon"><Plus size={14} /></span>
                  Create &ldquo;<strong>{name.trim()}</strong>&rdquo;
                  <span className="spotlight-create-hint">
                    as {mode === 'location' ? 'location' : 'asset'}
                  </span>
                </button>
              )}

              {!showCreateOption && filteredPresets.length === 0 && (
                <p className="spotlight-empty">No presets found.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickAddSpotlight;
